import React, { useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { CollapsibleResizableContainer, SearchField, trackEvent } from 'shared/base';

import { AllResourcesItem } from './components/allResourcesItem';
import { CategoryItem } from './components/categoryItem';
import { SubcategoryList } from './components/subcategoryList';
import { useExpandedCategories } from './hooks/useExpandedCategories';
import { ANY_CATEGORY_VALUE } from '../../../constants/tableState';
import { useSidebarFilter } from '../../../hooks/useSidebarFilter';
import { useTableStateContext } from '../../../hooks/useTableStateContext';
import { filterSidebarList } from '../../../utils/table';

export function AssetInventorySidebarFilter() {
  const { fetchingResourceCategories, resourceCategories, resourceCategoriesTotalCount } =
    useTableStateContext();
  const {
    resourceCategory,
    resourceType,
    setResourceCategory,
    setResourceCategoryToAll,
    setResourceType,
  } = useSidebarFilter();

  const [menuSearchTerm, setMenuSearchTerm] = useState<string>('');
  const { expandedMap, toggleCategory } = useExpandedCategories();

  const isSearching = !!menuSearchTerm;

  const filteredResourceCategories = useMemo(
    () => filterSidebarList(resourceCategories, menuSearchTerm),
    [menuSearchTerm, resourceCategories],
  );

  const clearMenuSearch = () => {
    setMenuSearchTerm('');
    setResourceCategoryToAll();
  };

  const onCategoryClick = (category: string) => {
    setResourceCategory(category);
    trackEvent('asset_category_viewed', { category_name: category });
  };

  const onTypeAndCategoryClick = (type: string) => {
    setResourceType(type);
    trackEvent('asset_type_viewed', { type_name: type });
  };

  return (
    <CollapsibleResizableContainer
      bgcolor="secondary.darkest"
      borderRight="1px solid"
      collapsible={true}
      display="flex"
      flexDirection="column"
      flexShrink={0}
      height="100%"
      maxWidth={{ sm: '40%', xs: '80%' }}
      minHeight="0"
      minWidth={300}
      resizable={true}
      sx={{
        borderRightColor: 'secondary.dark',
      }}
      width={300}
    >
      <Stack height="100%">
        <Stack gap={1.5} paddingX={3} paddingY={1.5}>
          <Typography variant="h6">Resources</Typography>
          <SearchField
            onChange={(e) => setMenuSearchTerm(e.target.value)}
            onClear={clearMenuSearch}
            placeholder="Search"
            size="small"
            value={menuSearchTerm}
          />
        </Stack>
        <List
          dense={true}
          sx={{
            '& .MuiListItemButton-root': {
              borderRadius: 1.5,
              py: 0.5,
            },
            height: '100%',
            overflowY: 'auto',
            px: 3,
            py: 0,
          }}
        >
          <AllResourcesItem
            isLoading={fetchingResourceCategories}
            isSelected={resourceCategory === ANY_CATEGORY_VALUE}
            onClick={setResourceCategoryToAll}
            totalCount={resourceCategoriesTotalCount}
          />
          {filteredResourceCategories.map((menuItem) => (
            <Box key={menuItem.value}>
              <CategoryItem
                isExpanded={expandedMap[menuItem.value ?? '']}
                isLoading={fetchingResourceCategories}
                isSearching={isSearching}
                menuItem={menuItem}
                onToggleExpand={() => toggleCategory(menuItem.value ?? '')}
              />
              {menuItem.subitems && (
                <SubcategoryList
                  categoryIcon={<menuItem.icon />}
                  categoryValue={menuItem.value ?? ''}
                  isExpanded={isSearching || expandedMap[menuItem.value ?? '']}
                  menuItem={menuItem}
                  onCategoryClick={onCategoryClick}
                  onTypeAndCategoryClick={onTypeAndCategoryClick}
                  resourceCategory={resourceCategory}
                  resourceType={resourceType}
                />
              )}
            </Box>
          ))}
        </List>
      </Stack>
    </CollapsibleResizableContainer>
  );
}
